import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button } from 'antd';

import { dates } from '~library/dates';
import { useGscSyncState } from './gsc-connection';
import { GSC_SETTINGS_ROUTE, SEARCH_PERFORMANCE_RECONNECT_ROUTE } from './SearchPerformanceRoutes';
import useTrackSearchPerformanceEvent from './useTrackSearchPerformanceEvent';

function lostPhrasing(propertyUrls: string[]) {
  return propertyUrls.length === 1
    ? { subject: propertyUrls[0], them: 'it', their: 'its', theyWere: 'it was' }
    : { subject: propertyUrls.join(', '), them: 'them', their: 'their', theyWere: 'they were' };
}

// Copy adapted from the Moonshot prototype (UX-Moonshot/src/pages/SearchPerformancePage.tsx
// → the `freshness.stale` alert, `SP.scope.staleTitle`/`formatStaleScope`).
const STRINGS = {
  title: 'These figures have stopped updating',
  accountFallbackName: 'This Google account',
  reconnect: 'Reconnect',
  description: (accountName: string, since: string | null) =>
    since
      ? `${accountName} needs reconnecting, so nothing has arrived from Google since ${since}. Pages that started appearing in Search after that date are counted as having no impressions here.`
      : `${accountName} needs reconnecting, so nothing has arrived from Google. Pages that started appearing in Search are counted as having no impressions here.`,
  lostTitle: (propertyUrls: string[]) =>
    propertyUrls.length === 1
      ? `${propertyUrls[0]} has stopped updating`
      : `${propertyUrls.length} properties have stopped updating`,
  lostDescription: (propertyUrls: string[], since: string | null, isLostOnly: boolean) => {
    const { subject, them, their, theyWere } = lostPhrasing(propertyUrls);
    const arrived = since ? `nothing has arrived for ${them} since ${since}` : `nothing has arrived for ${them}`;
    const fix = isLostOnly
      ? `Add ${them} back in Search Console and reconnect from Manage GSC domains, or disconnect the account from Integrations.`
      : `Remove ${them} under Manage GSC domains, or add ${them} back in Search Console and re-add ${them} there.`;
    return `Google Search Console no longer shares ${subject} with the connected account — ${theyWere} removed or ${their} permission was revoked — so ${arrived}. ${fix}`;
  },
  manageDomains: 'Manage GSC domains',
};

function SearchPerformanceReconnectAlert() {
  const navigate = useNavigate();
  const trackEvent = useTrackSearchPerformanceEvent();
  const { isBroken, brokenSinceAt, connectedAccountEmail, lostUrls, lostSinceAt, isLostOnly } = useGscSyncState();

  if (isBroken) {
    const handleReconnect = () => {
      trackEvent('Search Performance: Dashboard Reconnect Clicked');
      navigate(SEARCH_PERFORMANCE_RECONNECT_ROUTE);
    };

    return (
      <Alert
        type="warning"
        showIcon
        message={STRINGS.title}
        description={STRINGS.description(
          connectedAccountEmail ?? STRINGS.accountFallbackName,
          brokenSinceAt ? dates.pointInTimeWithAge(brokenSinceAt) : null
        )}
        action={
          <Button size="small" type="primary" onClick={handleReconnect}>
            {STRINGS.reconnect}
          </Button>
        }
      />
    );
  }

  if (lostUrls.length === 0) return null;

  const handleManageDomains = () => {
    trackEvent('Search Performance: Dashboard Manage Domains Clicked');
    navigate(GSC_SETTINGS_ROUTE);
  };

  return (
    <Alert
      type="warning"
      showIcon
      message={STRINGS.lostTitle(lostUrls)}
      description={STRINGS.lostDescription(lostUrls, lostSinceAt ? dates.pointInTimeWithAge(lostSinceAt) : null, isLostOnly)}
      action={
        <Button size="small" type="primary" onClick={handleManageDomains}>
          {STRINGS.manageDomains}
        </Button>
      }
    />
  );
}

export default SearchPerformanceReconnectAlert;
